// store/types.ts

import {
    Contact,
    Favorite,
    HashtagData,
    LocationData,
    LookalikesData,
} from '../../Apps/Hub/Recruit/types';
import { ProspectPlatform } from '../../clientContracts/prospects/brandProspects/entities';
import { ImaiUserProfile } from '../../clientContracts/prospects/imai/imaiProspectDCOs';
import { Genders, ImaiSearchDTO } from '../../clientContracts/prospects/imai/imaiProspectDTOs';
import { Tags } from '../../clientContracts/tags/entities';
import { AffiliateStatuses } from '../../clientContracts/tiktokShop/entities';
import { SequenceContact } from '../../types/sequence';

export interface ImaiSearchResultRow {
    account: {
        audience_source: string;
        user_profile: ImaiUserProfile & {
            recruitingStatus?: RecruitingStatus;
            platform: RecruitPlatform;
            handle: string;
        };
        contact?: Contact;
        favorite?: Favorite;
        search_result_id: string;
        hidden_result: boolean;
    };
    match: unknown; // Define this type more specifically if possible
    ttsData: {
        status: AffiliateStatuses;
    } & {
        username: string;
        nickname: string;
        avatar: {
            url: string;
        };
        selection_region: string;
        bio_description: string;
        follower_count: number;
        profile_tt_uri: string;
        category_ids: string[];
        top_collaborated_brand_ids: string[];
        brand_collaboration_count: number;
        units_sold?: number;
        units_sold_range?: {
            maximum_amount: number;
            minimum_amount: number;
        };
        gmv?: {
            amount: string;
            currency: string;
        };
        gmv_range?: {
            currency: string;
            maximum_amount: string;
            minimum_amount: string;
        };
        live_gmv?: {
            amount: string;
            currency: string;
        };
        video_gmv?: {
            amount: string;
            currency: string;
        };
        ec_live_engagement_rate: string;
        category_gmv_distribution: {
            category_id: string;
            value: string;
        }[];
        content_gmv_distribution: {
            content_type: string;
            value: string;
        }[];
        product_original_price_range: {
            currency: string;
            maximum_amount: string;
            minimum_amount: string;
        };
        gpm?: {
            currency: string;
            amount: string;
        };
        live_gpm?: {
            currency: string;
            amount: string;
        };
        video_gpm?: {
            currency: string;
            amount: string;
        };
        video_gpm_range?: {
            currency: string;
            maximum_amount: string;
            minimum_amount: string;
        };
        live_gpm_range?: {
            currency: string;
            maximum_amount: string;
            minimum_amount: string;
        };
        promoted_product_num: number;
        ec_live_count: number;
        ec_video_count: number;
        avg_ec_video_play_count: number;
        avg_commission_rate: number;
        avg_commission_rate_range: {
            maximum_amount: string;
            minimum_amount: string;
        };
        avg_gmv_per_buyer: {
            currency: string;
            amount: string;
        };
        avg_gmv_per_buyer_range: {
            currency: string;
            maximum_amount: string;
            minimum_amount: string;
        };
        avg_ec_live_view_count: number;
        avg_ec_live_like_count: number;
        avg_ec_live_comment_count: number;
        avg_ec_live_share_count: number;
        avg_ec_video_like_count: number;
        avg_ec_video_comment_count: number;
        avg_ec_video_share_count: number;
    };
}

export interface ProspectsState {
    searchResults: ImaiSearchResultRow[];
    favorites: Favorite[];
    contacts: Contact[];
    sequenceContacts: SequenceContact[];
    favoriteTags: Tags[];
    contactTags: Tags[];
    isLoading: boolean;
    searchFilters: ProspectSearchFilters;
    searchTotal: number;
    searchError: string;
    searchLoading: boolean;
    snackbar: SnackbarType;
    searchSummary: SearchSummary;
}
export interface SnackbarType {
    duration?: number;
    open: boolean;
    message: string;
}

// Action types
export const ADD_TO_FAVORITES = 'ADD_TO_FAVORITES';
export const REMOVE_FROM_FAVORITES = 'REMOVE_FROM_FAVORITES';
export const SET_SEARCH_RESULTS = 'SET_SEARCH_RESULTS';
export const SET_SEARCH_FILTERS = 'SET_SEARCH_FILTERS';
export const SET_SEARCH_TOTAL = 'SET_SEARCH_TOTAL';
export const SET_SEARCH_FAILURE = 'SET_SEARCH_FAILURE';
export const SET_SEARCH_LOADING = 'SET_SEARCH_LOADING';
export const ADD_TO_CONTACTS = 'ADD_TO_CONTACTS';
export const REMOVE_FROM_CONTACTS = 'REMOVE_FROM_CONTACTS';
export const SET_CONTACT_TAGS = 'SET_CONTACT_TAGS';
export const SET_FAVORITE_TAGS = 'SET_FAVORITE_TAGS';
export const SET_IS_LOADING = 'SET_IS_LOADING';
export const SET_SNACKBAR = 'SET_SNACKBAR';
export const SET_SEARCH_SUMMARY = 'SET_SEARCH_SUMMARY';
export const SET_SEQUENCE_CONTACTS = 'SET_SEQUENCE_CONTACTS';
export const REMOVE_FROM_SEQUENCE_CONTACTS = 'REMOVE_FROM_SEQUENCE_CONTACTS';

interface SetFavoritesAction {
    type: typeof ADD_TO_FAVORITES;
    payload: Favorite[];
}

interface RemoveFromFavoritesAction {
    type: typeof REMOVE_FROM_FAVORITES;
    payload: Favorite;
}

interface SetSearchResultsAction {
    type: typeof SET_SEARCH_RESULTS;
    payload: ImaiSearchResultRow[];
    loading: boolean;
}

interface SetSearchFiltersAction {
    type: typeof SET_SEARCH_FILTERS;
    payload: ProspectSearchFilters;
}

interface SetSearchTotalAction {
    type: typeof SET_SEARCH_TOTAL;
    payload: number;
}

interface SetSearchFailureAction {
    type: typeof SET_SEARCH_FAILURE;
    payload: string;
    loading: boolean;
}

interface SetSearchLoadingAction {
    type: typeof SET_SEARCH_LOADING;
    loading: boolean;
}
interface SetContactsAction {
    type: typeof ADD_TO_CONTACTS;
    payload: Contact[];
}
interface RemoveFromContactsAction {
    type: typeof REMOVE_FROM_CONTACTS;
    payload: Contact;
}
interface SetContactTagsAction {
    type: typeof SET_CONTACT_TAGS;
    payload: Tags[];
}
interface SetFavoriteTagsAction {
    type: typeof SET_FAVORITE_TAGS;
    payload: Tags[];
}
interface SetIsLoadingAction {
    type: typeof SET_IS_LOADING;
    payload: boolean;
}
interface SetSnackbarAction {
    type: typeof SET_SNACKBAR;
    payload: SnackbarType;
}

interface SetSearchSummaryAction {
    type: typeof SET_SEARCH_SUMMARY;
    payload: SearchSummary;
}

interface SetSequenceContactsAction {
    type: typeof SET_SEQUENCE_CONTACTS;
    payload: SequenceContact[];
}

interface RemoveFromSequenceContactsAction {
    type: typeof REMOVE_FROM_SEQUENCE_CONTACTS;
    payload: SequenceContact;
}

export type ProspectsActionTypes =
    | SetFavoritesAction
    | RemoveFromFavoritesAction
    | SetSearchResultsAction
    | SetSearchFiltersAction
    | SetSearchTotalAction
    | SetSearchFailureAction
    | SetSearchLoadingAction
    | SetContactsAction
    | RemoveFromContactsAction
    | SetContactTagsAction
    | SetFavoriteTagsAction
    | SetIsLoadingAction
    | SetSnackbarAction
    | SetSearchSummaryAction
    | SetSequenceContactsAction
    | RemoveFromSequenceContactsAction;

export type ProspectGender = Genders | 'ANY';

export type ProspectSearchFilters = ImaiSearchDTO & {
    is_hidden?: boolean;
    followerRange: {
        min?: string;
        max?: string;
    };
    skip?: number;
    skipHistory?: {
        [key: string]: number;
    };
};

export type RecruitPlatform = ProspectPlatform;

export type SearchSummary = {
    location: LocationData[];
    gender: string;
    followerRange: string;
    lookalikes: LookalikesData[];
    hashtags: HashtagData[];
    tiktokShopFilters?: {
        avgLiveUniqueVisitorsRange?: string;
        gmvRange?: string;
        liveGmvRange?: string;
        videoGmvRange?: string;
    };
};
